import { ContentfulArticleBlock, ContentfulHeroBlock, ContentfulHeroCta } from '@jbhi-fi/apis/src/contentful/types';
import { ContentBlockProps } from '../../ContentBlock/ContentBlock';
import { ThemeTransformer } from './ThemeTransformer';
import { ListingBlockHeading } from '../../ListingBlock/ListingBlockHeading';
import { HeroBlockTransformer } from './HeroBlockTransformer';
import { Icon } from '@jbhi-fi/lanyard-ui/components/Icon';
import React from 'react';
import { ContentfulImage } from '../../ContentfulImage/ContentfulImage';
import { AssetTransformer } from './AssetTransformer';
import { TabsBlockProps } from '../../TabsBlock/TabsBlock';
import { HeroBlock } from '../../HeroBlock';
import { useTrackingContext } from '../../Tracking';
import { CardTransformer } from './CardTransformer';
import { HeroTile } from '../../HeroTile/HeroTile';

export abstract class ArticleBlockTransformer {
  static toContentBlockProps = (entry: ContentfulArticleBlock): Partial<ContentBlockProps> => {
    const theme = ThemeTransformer.toContentThemeVariables(entry?.fields?.theme);

    return {
      style: { backgroundColor: theme?.palette?.background },
    };
  };

  static toTabsBlockProps = (entry: ContentfulArticleBlock, { testId }: { testId?: string } = {}): TabsBlockProps => {
    const theme = ThemeTransformer.toContentThemeVariables(entry?.fields?.theme);

    return {
      testId: testId,
      heading: entry?.fields?.headingLogo?.fields ? (
        <ContentfulImage {...AssetTransformer.toContentfulImageProps(entry?.fields?.headingLogo)} />
      ) : (
        <>
          {theme?.icon ? <Icon name={theme.icon} /> : null}
          <ListingBlockHeading heading={entry?.fields?.heading} />
        </>
      ),
      subHeading: entry?.fields?.subText,
      palette: theme?.palette ?? {},
      linkProps: entry?.fields?.button?.fields
        ? {
            label: entry.fields.button.fields.label,
            href: entry.fields.button.fields.url,
            shouldOpenInNewWindow: entry.fields.button.fields.shouldOpenInNewWindow,
          }
        : undefined,
      tabs:
        entry?.fields?.heroBlocks
          ?.filter(heroBlock => heroBlock?.fields)
          ?.map(heroBlock => ({
            label: heroBlock?.fields?.heading ?? '',
            contents: <ArticleBlockHeroBlockComponent entry={heroBlock} heroCta={entry?.fields?.miniBanner} />,
          })) ?? [],
    };
  };
}

export const ArticleBlockHeroBlockComponent = ({
  entry,
  heroCta,
  testId,
}: {
  entry: ContentfulHeroBlock;
  heroCta?: ContentfulHeroCta;
  testId?: string;
}) => {
  const { tracker } = useTrackingContext();
  const heroCtaTransformed = heroCta ? CardTransformer.toCardProps(heroCta) : null;
  const heroBlockProps = HeroBlockTransformer.toHeroBlockProps(entry, {
    heroBlockCta: heroCtaTransformed ? <HeroTile {...heroCtaTransformed} /> : undefined,
    testId: testId,
  });
  heroBlockProps.cards = heroBlockProps.cards?.map(cardProps => ({
    ...cardProps,
    onClick: (...args) => {
      tracker.trackClick({
        label: cardProps.heading,
        extraData: { contentCategory: cardProps.tagProps?.label, moduleName: 'Content card' },
      });
      cardProps.onClick?.(...args);
    },
  }));

  return <HeroBlock {...heroBlockProps} />;
};
